import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getSessionUser } from '../../services/userService';
import '../../cms.css';
import { 
    ArrowLeft, Save, Image as ImageIcon, Plus, Trash2, GripVertical, 
    X, Check, ChevronLeft, ChevronRight, Upload, Link as LinkIcon, Loader2, Copy, CornerDownRight, CheckSquare, Square,
    AlertTriangle, CheckCircle
} from 'lucide-react';
import MediaLibraryModal from './MediaLibraryModal';
import { useSeo } from '../../hooks/useSeo';

interface Category {
    id: number;
    name: string;
    slug: string;
    parent_id?: number | null;
}

interface MediaItem {
    id: number;
    url: string;
    url_webp?: string;
    type: number; // 1 = img
    size?: number; // Size in bytes
}

// --- HELPER HOOKS & FUNCTIONS ---

const useDebounce = (value: string, delay: number) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
};

const slugify = (str: string) => {
    str = str.replace(/^\s+|\s+$/g, ''); // trim
    str = str.toLowerCase();

    const charMap: { [key: string]: string } = {
        'ą': 'a', 'ć': 'c', 'ę': 'e', 'ł': 'l', 'ń': 'n', 'ó': 'o', 'ś': 's', 'ż': 'z', 'ź': 'z',
        'à': 'a', 'á': 'a', 'ä': 'a', 'â': 'a', 'ã': 'a', 'å': 'a', 'æ': 'a',
        'è': 'e', 'é': 'e', 'ë': 'e', 'ê': 'e',
        'ì': 'i', 'í': 'i', 'ï': 'i', 'î': 'i',
        'ñ': 'n',
        'ò': 'o', 'ö': 'o', 'ô': 'o', 'õ': 'o', 'ø': 'o',
        'š': 's',
        'ù': 'u', 'ú': 'u', 'ü': 'u', 'û': 'u',
        'ç': 'c',
        '·': '-', '/': '-', '_': '-', ',': '-', ':': '-', ';': '-'
    };
    
    // Create a regex from the map keys, escaping special regex characters
    const regex = new RegExp(Object.keys(charMap).map(c => c.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')).join('|'), 'g');
    
    str = str.replace(regex, (matched) => charMap[matched as keyof typeof charMap]);

    str = str.replace(/[^a-z0-9 -]/g, '') // remove invalid chars that were not in the map
        .replace(/\s+/g, '-') // collapse whitespace and replace by -
        .replace(/-+/g, '-'); // collapse dashes

    return str;
};


// --- HELPER COMPONENTS ---

// Simple Code Editor with Line Numbers and Highlighting
const CodeEditor: React.FC<{ value: string; onChange: (val: string) => void }> = ({ value, onChange }) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const preRef = useRef<HTMLPreElement>(null);
    const linesRef = useRef<HTMLDivElement>(null);

    const handleScroll = () => {
        if (textareaRef.current && preRef.current && linesRef.current) {
            preRef.current.scrollTop = textareaRef.current.scrollTop;
            preRef.current.scrollLeft = textareaRef.current.scrollLeft;
            linesRef.current.scrollTop = textareaRef.current.scrollTop;
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Tab') {
            e.preventDefault();
            const start = e.currentTarget.selectionStart;
            const end = e.currentTarget.selectionEnd;
            
            // Insert 2 spaces for soft tab
            const spaces = '  ';
            const newValue = value.substring(0, start) + spaces + value.substring(end);
            
            onChange(newValue);
            
            // Restore cursor position after state update
            setTimeout(() => {
                if (textareaRef.current) {
                    textareaRef.current.selectionStart = textareaRef.current.selectionEnd = start + spaces.length;
                }
            }, 0);
        }
    };

    const lineCount = useMemo(() => value.split('\n').length, [value]);

    // Simple HTML Highlighting Logic (Tokenizer)
    const highlightedCode = useMemo(() => {
        if (!value) return '';
        // Very basic regex tokenizer for HTML
        // Note: This is purely visual and doesn't handle nested complex states perfectly, but works for basic editing.
        let html = value
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');

        // Highlight Tags
        html = html.replace(/(&lt;\/?[a-z0-9]+)(?![^<]*&gt;)/gi, '<span class="token-tag">$1</span>');
        
        // Highlight Attributes (rough approximation)
        html = html.replace(/([a-z-]+)=/gi, '<span class="token-attr">$1</span>=');
        
        // Highlight Strings
        html = html.replace(/(".*?")/g, '<span class="token-string">$1</span>');
        
        // Highlight Comments
        html = html.replace(/(&lt;!--[\s\S]*?--&gt;)/g, '<span class="token-comment">$1</span>');

        // Finish Tag closing
        html = html.replace(/(\/?&gt;)/g, '<span class="token-tag">$1</span>');

        return html;
    }, [value]);

    return (
        <div className="code-editor-container border border-gray-700 h-full min-h-[500px]">
            {/* Line Numbers */}
            <div className="code-editor-lines" ref={linesRef}>
                {Array.from({ length: lineCount }).map((_, i) => (
                    <div key={i}>{i + 1}</div>
                ))}
            </div>
            
            {/* Visual Layer (Pre/Code) */}
            <pre className="code-editor-pre no-scrollbar" ref={preRef} aria-hidden="true">
                <code dangerouslySetInnerHTML={{ __html: highlightedCode + '<br />' }} />
            </pre>

            {/* Input Layer (Textarea) */}
            <textarea
                ref={textareaRef}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={handleKeyDown}
                onScroll={handleScroll}
                spellCheck="false"
                className="code-editor-textarea no-scrollbar"
                placeholder="<p>Enter HTML content here...</p>"
            />
        </div>
    );
};

// Media Item Component with Metadata
const MediaGalleryItem: React.FC<{ 
    media: MediaItem; 
    index: number; 
    total: number;
    onRemove: () => void; 
    onMove: (dir: 'left' | 'right') => void;
}> = ({ media, index, total, onRemove, onMove }) => {
    const [dimensions, setDimensions] = useState<string>('');
    const [fileSizeStr, setFileSizeStr] = useState<string>('');
    const [isCopied, setIsCopied] = useState(false);

    useEffect(() => {
        // 1. If size comes from DB, use it
        if (media.size) {
            setFileSizeStr(formatBytes(media.size));
            return;
        }

        // 2. Fallback: If no size in DB (legacy images), try to estimate
        if (media.url.startsWith('data:')) {
            // Base64 estimation
            const sizeInBytes = 4 * Math.ceil((media.url.length / 3)) * 0.5624896334383812;
            setFileSizeStr(formatBytes(sizeInBytes));
        } else {
            // 3. Last resort: Remote URL HEAD request (often blocked or slow, but keep as fallback)
            fetch(media.url, { method: 'HEAD' })
                .then(res => {
                    const size = res.headers.get('content-length');
                    if (size) {
                        setFileSizeStr(formatBytes(parseInt(size)));
                    }
                })
                .catch(() => setFileSizeStr('?'));
        }
    }, [media.url, media.size]);

    const formatBytes = (bytes: number) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        const img = e.currentTarget;
        setDimensions(`${img.naturalWidth}x${img.naturalHeight}`);
    };

    const copyLink = (e: React.MouseEvent) => {
        e.stopPropagation(); // prevent drag/click issues
        navigator.clipboard.writeText(media.url);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    };

    return (
        <div className="relative bg-mystic-inset rounded border border-gray-700 group overflow-hidden flex flex-col h-full">
            <div className="relative aspect-square w-full bg-mystic-inset">
                <img 
                    src={media.url} 
                    alt="" 
                    onLoad={handleImageLoad}
                    className="w-full h-full object-contain" 
                />
                
                {/* Overlay Actions */}
                <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-2 z-10">
                    <div className="flex justify-between items-start">
                        {/* Index Badge */}
                        <div className="bg-black/50 text-white text-[10px] px-1.5 rounded border border-gray-600">
                            #{index + 1}
                        </div>
                        <button onClick={onRemove} className="text-red-400 hover:text-red-300 p-1 bg-black/50 rounded">
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                    
                    <div className="flex justify-center">
                        <button 
                            onClick={copyLink} 
                            className="bg-mystic-accent hover:bg-mystic-accentHover text-black px-3 py-1 rounded text-xs font-bold flex items-center gap-1 transition-transform active:scale-95"
                        >
                            {isCopied ? <Check className="w-3 h-3" /> : <LinkIcon className="w-3 h-3" />}
                            {isCopied ? 'Copied!' : 'Copy Link'}
                        </button>
                    </div>

                    <div className="flex justify-between">
                        <button 
                            onClick={() => onMove('left')} 
                            disabled={index === 0}
                            className="text-white hover:text-mystic-accent disabled:opacity-30 p-1"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button 
                            onClick={() => onMove('right')} 
                            disabled={index === total - 1}
                            className="text-white hover:text-mystic-accent disabled:opacity-30 p-1"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Metadata Footer */}
            <div className="p-2 bg-mystic-inset border-t border-gray-800 text-[10px] text-gray-400 font-mono flex justify-between items-center mt-auto">
                <span>{dimensions || '...'}</span>
                <span className="text-gray-500">{fileSizeStr}</span>
            </div>
        </div>
    );
};

const ProductEditor: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const user = getSessionUser();
    
    // Form State
    const [name, setName] = useState('');
    const [slug, setSlug] = useState('');
    const [price, setPrice] = useState<number>(0);
    const [description, setDescription] = useState('');
    const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
    const [tags, setTags] = useState('');

    // Slug Validation State
    const debouncedSlug = useDebounce(slug, 500);
    const [slugStatus, setSlugStatus] = useState<'checking' | 'available' | 'taken' | 'idle'>('idle');
    const [slugError, setSlugError] = useState('');
    
    // Media State
    const [attachedMedia, setAttachedMedia] = useState<MediaItem[]>([]);
    const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
    
    // Resources State
    const [categories, setCategories] = useState<Category[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const adminPath = location.pathname.split('/')[1];
    
    // Set title
    useSeo({ title: `CMS - ${id ? `Edit Product #${id}` : 'New Product'} - esoteric.pl` });

    useEffect(() => {
        if (!user || user.rank !== 0) {
            navigate('/404');
            return;
        }
        
        loadResources();
        if (id) {
            loadProduct(id);
        }
    }, [id]);

    // Auto-generate slug from name for new products
    useEffect(() => {
        if (!id) {
            setSlug(slugify(name));
        }
    }, [name, id]);

    // Slug validation effect
    useEffect(() => {
        if (debouncedSlug) {
            validateSlug(debouncedSlug);
        } else {
            setSlugStatus('idle');
        }
    }, [debouncedSlug, id]);

    const loadResources = async () => {
        try {
            const catRes = await fetch('/api/shop/categories');
            if (catRes.ok) setCategories(await catRes.json());
        } catch (e) {
            console.error(e);
        }
    };

    const loadProduct = async (prodId: string) => {
        try {
            const res = await fetch(`/api/shop/products?id=${prodId}`);
            if (res.ok) {
                const data = await res.json();
                setName(data.name);
                setSlug(data.slug || slugify(data.name));
                setPrice(data.price);
                setDescription(data.description || '');
                setTags((data.tags || []).join(', '));
                
                // Parse Categories
                try {
                    const catIds = JSON.parse(data.category || '[]');
                    setSelectedCategories(catIds);
                } catch { setSelectedCategories([]); }

                // Parse Media
                if (data.media && Array.isArray(data.media)) {
                    setAttachedMedia(data.media);
                }
            }
        } catch (e) { console.error(e); }
    };

    const validateSlug = async (slugToCheck: string) => {
        setSlugStatus('checking');
        setSlugError('');
        try {
            const res = await fetch(`/api/slugs?check=${slugToCheck}&currentId=${id}&type=product`);
            const data = await res.json();
            if (data.available) {
                setSlugStatus('available');
            } else {
                setSlugStatus('taken');
                setSlugError(`Slug taken by a ${data.type}`);
            }
        } catch (e) {
            setSlugStatus('idle');
            setSlugError('Validation error');
        }
    };

    const handleSave = async () => {
        if (slugStatus === 'taken') {
            alert("Slug is already taken. Please choose another one.");
            return;
        }
        setIsLoading(true);
        setIsSuccess(false);
        const payload = {
            id: id ? parseInt(id) : undefined,
            name,
            slug,
            price,
            description,
            category: JSON.stringify(selectedCategories),
            media: JSON.stringify(attachedMedia.map(m => m.id)),
            tags: tags.split(',').map(t => t.trim()).filter(Boolean)
        };

        const method = id ? 'PUT' : 'POST';
        try {
            const res = await fetch('/api/shop/products', {
                method,
                headers: { 
                    'Content-Type': 'application/json',
                    'x-admin-phone': user?.phone || '' 
                },
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                const responseData = await res.json();
                setIsSuccess(true);
                
                // If it was a new product, switch URL to edit mode silently
                if (!id && responseData.id) {
                    navigate(`/${adminPath}/products/edit/${responseData.id}`, { replace: true });
                }

                // Reset success state after a delay
                setTimeout(() => setIsSuccess(false), 2000);
            } else {
                alert('Save failed');
            }
        } catch (e) {
            console.error(e);
            alert('Error saving');
        } finally {
            setIsLoading(false);
        }
    };

    // --- Media Handlers ---
    const handleAddFromLibrary = (item: MediaItem) => {
        if (!attachedMedia.some(m => m.id === item.id)) {
            setAttachedMedia(prev => [...prev, item]);
        }
        setIsMediaModalOpen(false);
    };

    const handleRemoveMedia = (index: number) => {
        setAttachedMedia(prev => prev.filter((_, i) => i !== index));
    };

    const moveMedia = (index: number, direction: 'left' | 'right') => {
        if (direction === 'left' && index === 0) return;
        if (direction === 'right' && index === attachedMedia.length - 1) return;
        
        const newArr = [...attachedMedia];
        const targetIndex = direction === 'left' ? index - 1 : index + 1;
        const temp = newArr[targetIndex];
        newArr[targetIndex] = newArr[index];
        newArr[index] = temp;
        setAttachedMedia(newArr);
    };

    // Recursive Category Renderer
    const renderCategoryTree = (parentId: number | null, level = 0) => {
        const children = categories.filter(c => (c.parent_id === parentId) || (parentId === null && !c.parent_id));
        
        if (children.length === 0) return null;

        return (
            <div className={`flex flex-col gap-1 ${level > 0 ? 'ml-4 border-l border-gray-800 pl-2' : ''}`}>
                {children.map(cat => (
                    <React.Fragment key={cat.id}>
                        <label className="flex items-center gap-2 p-1.5 hover:bg-white/5 rounded cursor-pointer group transition-colors">
                            <div className="relative flex items-center">
                                {level > 0 && <span className="absolute -left-2 top-1/2 w-2 h-px bg-gray-700"></span>}
                                <div className={`w-4 h-4 border rounded flex items-center justify-center transition-colors shrink-0 ${selectedCategories.includes(cat.id) ? 'bg-mystic-accent border-mystic-accent' : 'border-gray-600 group-hover:border-gray-400'}`}>
                                    {selectedCategories.includes(cat.id) && <Check className="w-3 h-3 text-black" />}
                                </div>
                            </div>
                            
                            <input 
                                type="checkbox" 
                                className="hidden"
                                checked={selectedCategories.includes(cat.id)}
                                onChange={() => {
                                    if (selectedCategories.includes(cat.id)) {
                                        setSelectedCategories(prev => prev.filter(c => c !== cat.id));
                                    } else {
                                        setSelectedCategories(prev => [...prev, cat.id]);
                                    }
                                }}
                            />
                            <span className={`text-sm ${level === 0 ? 'font-bold text-white' : 'text-gray-400'}`}>
                                {cat.name}
                            </span>
                        </label>
                        {renderCategoryTree(cat.id, level + 1)}
                    </React.Fragment>
                ))}
            </div>
        );
    };

    // Main layout is h-screen overflow-hidden to keep header fixed
    return (
        <div className="h-screen overflow-hidden bg-mystic-bg text-[#e3e3e3] font-mono flex flex-col">
            
            {/* Header - Flex None to stay fixed size */}
            <header className="flex-none h-16 border-b border-gray-800 bg-mystic-box px-6 flex items-center justify-between z-40 w-full">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate(`/${adminPath}/products`)} className="p-2 hover:bg-gray-800 rounded-full text-gray-400 hover:text-white transition-colors">
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <h1 className="text-lg font-bold text-white uppercase tracking-wider">
                        {id ? `Edit Product #${id}` : 'New Product'}
                    </h1>
                </div>
                <button 
                    onClick={handleSave} 
                    disabled={isLoading || slugStatus === 'taken'}
                    className={`flex items-center gap-2 font-bold px-6 py-2 rounded transition-all disabled:opacity-50 ${isSuccess ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-mystic-accent hover:bg-mystic-accentHover text-black'}`}
                >
                    {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : isSuccess ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />} 
                    {isSuccess ? 'Saved' : 'Save'}
                </button>
            </header>

            {/* Main Content - Flex-1 and Overflow-Auto for internal scrolling */}
            <main className="flex-1 overflow-y-auto p-6 w-full">
                <div className="grid grid-cols-12 gap-6 pb-10">
                    {/* LEFT COLUMN (approx 83%): Basic Info -> Media -> Description */}
                    <div className="col-span-12 lg:col-span-10 space-y-6">
                        
                        {/* 1. Basic Info */}
                        <section className="bg-mystic-box border border-gray-800 p-6 rounded-lg space-y-4">
                            <h2 className="text-gray-500 text-xs uppercase font-bold tracking-widest mb-4">Basic Information</h2>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-gray-400 text-xs mb-1">Product Name</label>
                                    <input 
                                        type="text" 
                                        value={name} 
                                        onChange={e => setName(e.target.value)} 
                                        className="w-full bg-mystic-inset border border-gray-700 rounded p-2 text-white outline-none focus:border-mystic-accent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-gray-400 text-xs mb-1">Slug (URL)</label>
                                    <div className="relative">
                                        <input 
                                            type="text" 
                                            value={slug} 
                                            onChange={e => setSlug(slugify(e.target.value))} 
                                            className="w-full bg-mystic-inset border border-gray-700 rounded p-2 text-white outline-none focus:border-mystic-accent"
                                        />
                                        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs flex items-center gap-1">
                                            {slugStatus === 'checking' && <Loader2 className="w-3 h-3 animate-spin text-gray-500"/>}
                                            {slugStatus === 'available' && <CheckCircle className="w-3 h-3 text-green-500"/>}
                                            {slugStatus === 'taken' && <AlertTriangle className="w-3 h-3 text-red-500"/>}
                                        </div>
                                    </div>
                                    {slugError && <p className="text-red-500 text-xs mt-1">{slugError}</p>}
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-gray-400 text-xs mb-1">Price (PLN)</label>
                                    <input 
                                        type="number" 
                                        step="0.01"
                                        value={price} 
                                        onChange={e => setPrice(parseFloat(e.target.value))} 
                                        className="w-full bg-mystic-inset border border-gray-700 rounded p-2 text-white outline-none focus:border-mystic-accent font-mono"
                                    />
                                </div>
                                <div>
                                     <label className="block text-gray-400 text-xs mb-1">Tags (comma-separated)</label>
                                     <input 
                                        type="text"
                                        value={tags}
                                        onChange={e => setTags(e.target.value)}
                                        placeholder="np. kryształ, ametyst, energia"
                                        className="w-full bg-mystic-inset border border-gray-700 rounded p-2 text-white outline-none focus:border-mystic-accent"
                                    />
                                </div>
                            </div>
                        </section>

                        {/* 2. Media Gallery (Moved here) */}
                        <section className="bg-mystic-box border border-gray-800 p-6 rounded-lg">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-gray-500 text-xs uppercase font-bold tracking-widest">Media Gallery</h2>
                                <button 
                                    onClick={() => setIsMediaModalOpen(true)}
                                    className="text-xs bg-gray-800 hover:bg-gray-700 text-white px-3 py-1.5 rounded flex items-center gap-1 transition-colors border border-gray-700"
                                >
                                    <Plus className="w-3 h-3" /> Add Media
                                </button>
                            </div>

                            {/* Wider Grid for Media */}
                            <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-4">
                                {attachedMedia.map((media, index) => (
                                    <MediaGalleryItem 
                                        key={index}
                                        media={media}
                                        index={index}
                                        total={attachedMedia.length}
                                        onRemove={() => handleRemoveMedia(index)}
                                        onMove={(dir) => moveMedia(index, dir)}
                                    />
                                ))}
                                {attachedMedia.length === 0 && (
                                    <div className="col-span-full py-12 text-center border-2 border-dashed border-gray-800 rounded text-gray-600 text-sm flex flex-col items-center justify-center gap-2">
                                        <ImageIcon className="w-8 h-8 opacity-20" />
                                        No media attached. Click "Add Media" to upload or select.
                                    </div>
                                )}
                            </div>
                        </section>

                        {/* 3. Description Editor */}
                        <section className="bg-mystic-box border border-gray-800 p-6 rounded-lg flex flex-col min-h-[600px]">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-gray-500 text-xs uppercase font-bold tracking-widest">Description (HTML Code)</h2>
                                <div className="text-[10px] text-gray-600 font-mono">
                                    Editor: HTML Mode
                                </div>
                            </div>
                            <div className="flex-1 min-h-0">
                                <CodeEditor 
                                    value={description} 
                                    onChange={setDescription} 
                                />
                            </div>
                        </section>
                    </div>

                    {/* RIGHT COLUMN (approx 17%): Categories only */}
                    <div className="col-span-12 lg:col-span-2">
                        <section className="bg-mystic-box border border-gray-800 p-4 rounded-lg sticky top-0 max-h-full overflow-y-auto custom-scrollbar">
                            <h2 className="text-gray-500 text-xs uppercase font-bold tracking-widest mb-4 border-b border-gray-800 pb-2">Categories</h2>
                            
                            <div className="space-y-1">
                                {categories.length > 0 ? renderCategoryTree(null) : (
                                    <p className="text-gray-600 text-xs italic">No categories found.</p>
                                )}
                            </div>
                        </section>
                    </div>
                </div>
            </main>

            {user && (
              <MediaLibraryModal
                isOpen={isMediaModalOpen}
                onClose={() => setIsMediaModalOpen(false)}
                onSelectMedia={handleAddFromLibrary}
                currentUser={user}
                contextName={name || 'product'}
              />
            )}
        </div>
    );
};

export default ProductEditor;